import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Building2, ChevronDown, Menu, X } from "lucide-react";
import { LANGS, useLang } from "@/lib/i18n";
import { alternatePath, lp } from "@/lib/seoContent";
import MilesLogo from "@/components/Logo";

export const Logo = ({ light = false }) => {
  const { lang } = useLang();
  return (
    <Link to={lp(lang, "/")} data-testid="nav-logo" aria-label="MILES — retour à l'accueil" className="inline-flex items-center">
      <MilesLogo variant={light ? "white" : "blue"} className="h-[22px]" testId="nav-logo-img" />
    </Link>
  );
};

export const LangSwitcher = ({ light = false }) => {
  const { lang, setLang } = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  const switchTo = (l) => {
    // pages localisées : navigation vers l'URL traduite (hreflang réel, crawlable) — sinon simple état
    if (/^\/(fr|en|de)(\/|$)/.test(location.pathname)) {
      navigate(alternatePath(location.pathname, l));
    }
    setLang(l);
  };
  return (
    <div
      data-testid="lang-switcher"
      className={`flex items-center rounded-full border p-0.5 ${light ? "border-white/25" : "border-ink/10"}`}
    >
      {LANGS.map((l) => (
        <button
          key={l}
          onClick={() => switchTo(l)}
          data-testid={`lang-${l}`}
          className={`rounded-full px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.1em] transition-colors duration-300 ${
            lang === l
              ? "bg-miles text-white"
              : light
                ? "text-white/60 hover:text-white"
                : "text-slate hover:text-ink"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

// Mobile : bouton langue carré, même design que le burger, à sa gauche.
export const MobileLangButton = ({ light = false }) => {
  const { lang, setLang } = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const switchTo = (l) => {
    if (/^\/(fr|en|de)(\/|$)/.test(location.pathname)) navigate(alternatePath(location.pathname, l));
    setLang(l);
    setOpen(false);
  };
  return (
    <div ref={ref} className="relative sm:hidden">
      <button
        type="button"
        data-testid="nav-mobile-lang-button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Choisir la langue"
        aria-expanded={open}
        className={`flex h-11 w-11 items-center justify-center gap-0.5 rounded-xl border font-mono text-[11px] font-semibold uppercase tracking-[0.05em] transition-colors duration-300 ${
          light ? "border-white/30 bg-white/10 text-white" : "border-ink/10 bg-white/80 text-ink"
        }`}
      >
        {lang}
        <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${open ? "rotate-180" : ""}`} strokeWidth={2} />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-24 overflow-hidden rounded-xl border border-ink/10 bg-white p-1 shadow-[0_16px_40px_rgba(16,24,40,0.14)]" data-testid="nav-mobile-lang-menu">
          {LANGS.map((l) => (
            <button
              key={l}
              type="button"
              onClick={() => switchTo(l)}
              data-testid={`nav-mobile-lang-${l}`}
              className={`flex w-full items-center rounded-lg px-3 py-2 font-mono text-[12px] font-semibold uppercase tracking-[0.1em] transition-colors ${
                lang === l ? "bg-miles-ice text-miles" : "text-ink hover:bg-ink/[0.05]"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function Navbar() {
  const { t, lang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [barInView, setBarInView] = useState(true);
  const [footerInView, setFooterInView] = useState(false);
  const [far, setFar] = useState(false);
  const location = useLocation();
  const path = location.pathname.replace(/\/+$/, "") || "/";
  const immersive = ["/reservation", "/suivi", "/business/app"].some((p) => path.startsWith(p));
  const isLocaleHome = /^\/(fr|en|de)$/.test(path);
  const darkHero = isLocaleHome || /^\/(fr|en|de)\/(transfert-aeroport|airport-transfer|flughafentransfer|business)$/.test(path);
  const lightText = darkHero && !scrolled && !open;
  const isHome = isLocaleHome;

  const LINKS = [
    { label: t("nav.services"), href: lp(lang, "/services") },
    { label: t("nav.fleet"), href: lp(lang, "/flotte") },
    { label: t("nav.drivers"), href: lp(lang, "/chauffeurs") },
    { label: t("nav.cities"), href: lp(lang, "/villes") },
    { label: t("nav.faq"), href: lp(lang, "/faq") },
    { label: t("nav.guides"), href: lp(lang, "/guides") },
    { label: t("nav.track"), href: "/suivi" },
  ];

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 32);
      setFar(window.scrollY > 500);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    const el = document.getElementById("booking-bar");
    const footer = document.querySelector('[data-testid="footer"]');
    const observers = [];
    if (el) {
      const obs = new IntersectionObserver(([e]) => setBarInView(e.isIntersecting), { threshold: 0.15 });
      obs.observe(el);
      observers.push(obs);
    } else {
      setBarInView(false);
    }
    if (footer) {
      const fobs = new IntersectionObserver(([e]) => setFooterInView(e.isIntersecting), { threshold: 0.05 });
      fobs.observe(footer);
      observers.push(fobs);
    } else {
      setFooterInView(false);
    }
    return () => observers.forEach((o) => o.disconnect());
  }, [location.pathname]);

  const showSmartCta = !immersive && !footerInView && (isHome ? !barInView : far);

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,box-shadow] duration-500 ${
          scrolled || open ? "border-b border-ink/5 bg-white/90 shadow-[0_8px_32px_rgba(16,24,40,0.05)] backdrop-blur-xl" : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className={`mx-auto flex max-w-7xl items-center justify-between px-5 transition-[height] duration-500 sm:px-8 ${scrolled ? "h-16" : "h-[76px]"}`}>
          <Logo light={lightText} />

          <nav className="hidden items-center gap-7 xl:flex" data-testid="nav-links">
            {LINKS.map((l) => (
              <Link
                key={l.href}
                to={l.href}
                data-testid={`nav-link-${l.href.slice(1)}`}
                className={`text-sm font-medium transition-colors duration-300 ${
                  lightText ? "text-white/80 hover:text-white" : "text-ink/65 hover:text-ink"
                }`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <div className="hidden sm:block"><LangSwitcher light={lightText} /></div>
            <Link
              to={lp(lang, "/business")}
              data-testid="nav-business-tool"
              className={`hidden items-center gap-2 rounded-full border px-5 py-3 text-sm font-semibold shadow-[0_10px_28px_rgba(7,12,26,0.14)] transition-[border-color,color,background-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 md:inline-flex ${
                lightText
                  ? "border-white bg-white text-miles-deep hover:bg-miles-ice hover:shadow-[0_14px_36px_rgba(255,255,255,0.2)]"
                  : "border-ink/10 bg-white text-miles-deep hover:border-miles/25 hover:text-miles"
              }`}
            >
              <Building2 className="h-4 w-4" strokeWidth={1.5} />
              {t("nav.business")}
            </Link>
            <Link
              to="/reservation"
              data-testid="nav-booking-cta"
              className="hidden items-center gap-2 rounded-full bg-miles px-6 py-3 text-sm font-semibold text-white transition-all duration-300 hover:bg-miles-dark sm:inline-flex"
            >
              {t("nav.book")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
            </Link>
            <MobileLangButton light={lightText} />
            <button
              data-testid="nav-mobile-menu-button"
              onClick={() => setOpen(!open)}
              aria-label="Menu"
              className={`flex h-11 w-11 items-center justify-center rounded-xl border transition-colors duration-300 xl:hidden ${
                lightText ? "border-white/30 bg-white/10 text-white" : "border-ink/10 bg-white/80 text-ink"
              }`}
            >
              {open ? <X className="h-5 w-5" strokeWidth={1.5} /> : <Menu className="h-5 w-5" strokeWidth={1.5} />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden border-t border-ink/5 bg-white xl:hidden"
              data-testid="nav-mobile-menu"
            >
              <div className="px-5 py-4">
                {LINKS.map((l) => (
                  <Link key={l.href} to={l.href} className="block border-b border-ink/5 py-4 text-base font-medium text-ink">
                    {l.label}
                  </Link>
                ))}
                <Link
                  to="/reservation"
                  data-testid="nav-mobile-booking-cta"
                  className="mt-2 flex items-center justify-center gap-2 rounded-2xl bg-miles py-4 text-sm font-semibold text-white"
                >
                  {t("nav.book_ride")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <AnimatePresence>
        {showSmartCta && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.92 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="fixed bottom-5 left-1/2 z-40 -translate-x-1/2 sm:hidden"
            data-testid="mobile-bottom-bar"
          >
            <Link
              to="/reservation"
              data-testid="mobile-bottom-booking-cta"
              className="flex items-center gap-2 rounded-full bg-miles px-7 py-3.5 text-sm font-semibold text-white shadow-[0_16px_40px_rgba(36,87,255,0.45)]"
            >
              {t("nav.book")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
